import { useState } from 'react'
import { Database } from 'lucide-react'
import { cn } from '@/lib/utils'
import { COLLECTION_REGISTRY } from '../config/collectionRegistry'
import { CollectionBrowser } from '../components/CollectionBrowser'
import {
  listPersonalizedMappings,
  createPersonalizedMapping,
  updatePersonalizedMapping,
  deletePersonalizedMapping,
} from '../api/personalizedMappings'
import type { PersonalizedMapping, CreatePersonalizedMappingRequest } from '../types'

/**
 * Maps each collection ID to its API functions.
 * To add a new collection: register it in collectionRegistry.ts, add its API
 * functions here, and add its API file under /api/.
 */
const COLLECTION_API: Record<
  string,
  {
    listFn: (accId: number) => Promise<Record<string, unknown>[]>
    createFn: (accId: number, data: Record<string, unknown>) => Promise<unknown>
    updateFn: (id: string, data: Record<string, unknown>) => Promise<unknown>
    deleteFn: (id: string) => Promise<unknown>
  }
> = {
  'personalized-mappings': {
    listFn: (accId) =>
      listPersonalizedMappings(accId).then((rows) =>
        rows.map((r: PersonalizedMapping) => ({
          id: r.id,
          accId: r.accId,
          tokenName: r.tokenName,
          displayName: r.displayName,
          sourceType: r.sourceType,
          entity: r.entity,
          eventName: r.eventName ?? '',
          category: r.category,
          type: r.type,
          attributeName: r.attributeName,
          dataType: r.dataType,
          fallbackValue: r.fallbackValue,
          aggregation: r.aggregation,
          timeWindow: r.timeWindow ?? '',
          status: r.status,
        })),
      ),
    createFn: (accId, data) =>
      createPersonalizedMapping({ ...data, accId } as unknown as CreatePersonalizedMappingRequest),
    updateFn: (id, data) => updatePersonalizedMapping(id, data),
    deleteFn: (id) => deletePersonalizedMapping(id),
  },
}

export function ProductDataPage() {
  const [activeTab, setActiveTab] = useState(COLLECTION_REGISTRY[0].id)

  const activeConfig = COLLECTION_REGISTRY.find((c) => c.id === activeTab)!
  const activeFns = COLLECTION_API[activeTab]

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Database className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Data</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage CDP configuration collections for your account.
          </p>
        </div>
      </div>

      {COLLECTION_REGISTRY.length > 1 && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-1">
            {COLLECTION_REGISTRY.map((col) => (
              <button
                key={col.id}
                onClick={() => setActiveTab(col.id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                  activeTab === col.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                {col.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {activeFns ? (
        <CollectionBrowser
          config={activeConfig}
          listFn={activeFns.listFn}
          createFn={activeFns.createFn}
          updateFn={activeFns.updateFn}
          deleteFn={activeFns.deleteFn}
        />
      ) : (
        <div className="p-12 text-center">
          <p className="text-sm text-gray-500">Collection not configured.</p>
        </div>
      )}
    </div>
  )
}
